import { useEffect, useRef, useState } from 'react';

import * as _ from 'lodash';

import { AxiosResponse } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Configuration,
  PricingApi,
  PricingQuoteRequestInput,
  PricingQuoteResponseOutput,
  ClientType,
  ClientBudget,
  ClientComplexity,
  ResearchComplexity,
  BusinessUnit,
  DistrictOutput,
  UniverseType,
  OverSampleType,
  DistrictDemoData,
} from '../../lib/pricing-tool-api';

export interface Foo {
  bar: string;
}

function exhaustiveGuard(_value: never): never {
  throw new Error(
    `ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(
      _value,
    )}`,
  );
}

export const decideClientComplexitySuggestions = (
  businessUnit: BusinessUnit,
): ClientComplexity[] => {
  if (businessUnit === BusinessUnit.Embold) {
    return [ClientComplexity.High, ClientComplexity.Ultra];
  }
  return [];
};

export const isClientBudgetMismatched = (
  clientBudget: ClientBudget,
  suggestedClientBudgets: ClientBudget[],
): boolean => {
  if (suggestedClientBudgets.length === 0) {
    return false;
  }
  return !suggestedClientBudgets.includes(clientBudget);
};

export const decideClientBudgetSuggestions = (
  clientType: ClientType,
  registeredVoterPopulation: number,
): ClientBudget[] => {
  switch (clientType) {
    case ClientType.Downballot:
      if (registeredVoterPopulation < 300_000) {
        return [ClientBudget.Low];
      }
      return [ClientBudget.Medium];
    case ClientType.HouseOrStatewide:
      return [ClientBudget.Medium];
    case ClientType.NationalPacIeAdvocacyOrg:
      return [ClientBudget.Medium, ClientBudget.High];
    case ClientType.StateLocalPacIeAdvocacyOrg:
      return registeredVoterPopulation > 4_000_000
        ? [ClientBudget.Medium, ClientBudget.High]
        : [ClientBudget.Medium, ClientBudget.Low];
    case ClientType.Corporate:
      return [ClientBudget.Medium, ClientBudget.High];
    default:
      return exhaustiveGuard(clientType);
  }
};

export const calculateGeneralMaxRecommendedSampleSize = (
  districtDemoDataList: DistrictDemoData[],
) => {
  if (districtDemoDataList.length === 0) {
    return 0;
  }
  return _.ceil(_.sumBy(districtDemoDataList, (d) => d.max_sample_size));
};

export const calculateDemPrimaryMaxRecommendedSampleSize = (
  districtDemoDataList: DistrictDemoData[],
) => {
  if (districtDemoDataList.length === 0) {
    return 0;
  }
  return _.ceil(
    _.sumBy(districtDemoDataList, (d) => d.dem_primary_max_sample_size ?? 0),
  );
};

export const calculateMaxRecommendedSampleSize = (
  districtDemoDataList: DistrictDemoData[],
  universeTypes: UniverseType[] = [],
) => {
  if (districtDemoDataList.length === 0) {
    return 0;
  }
  if (universeTypes.includes(UniverseType.DemPrimary)) {
    return calculateDemPrimaryMaxRecommendedSampleSize(districtDemoDataList);
  }
  return calculateGeneralMaxRecommendedSampleSize(districtDemoDataList);
};

// TODO: convert to zod
export const canPriceQuote = (request: PricingQuoteRequestInput) => {
  const totalUniverses = request.num_of_additional_crosstabs + request.num_of_additional_weighting_universes + request.num_of_additional_monitoring_universes + 1;
  return totalUniverses > 0 &&
  request.universe_types.length > 0 &&
    request.universe_types.length <= totalUniverses &&
  request.districts.length > 0 &&
  request.sample_sizes.length > 0 &&
  request.number_of_questions.length > 0 &&
  (!request.universe_types.includes(UniverseType.OtherNonStandard) ||
    request.other_universe_type_description !== '') &&
  request.special_demo_filter_pct &&
  request.special_demo_filter_pct >= 0.0 &&
  request.special_demo_filter_pct <= 1.0;
};

const UNIVERSE_TYPE_FILTER_PCT = new Map<string, number>([
  [UniverseType.OddYearGeneral, 0.8],
  [UniverseType.PrimaryLocalSpecialElection, 0.6],
  [UniverseType.DemPrimary, 0.6],
  [UniverseType.LocalSpecialElection, 0.6],
  [UniverseType.RegisteredVoters, 1.0],
  [UniverseType.EvenYearGeneral, 1.0],
  [UniverseType.OtherNonStandard, 1.0],
  [UniverseType.AllAdults, 1.0],
]);

const decideSuggestedSpecialDemoFilterPct = (
  universeTypes: UniverseType[],
): number => {
  // find all the filter percentages
  const relevantFilterPercentages = universeTypes.filter((ut) =>
    UNIVERSE_TYPE_FILTER_PCT.has(ut),
  );

  // using 1.0 as a default so there's always a valid value
  if (relevantFilterPercentages.length === 0) {
    return 1.0;
  }

  // return the most strict filter pct
  const minPct = Math.min(
    ...relevantFilterPercentages.map(
      (ut) => UNIVERSE_TYPE_FILTER_PCT.get(ut) as number,
    ),
  );
  return minPct;
};

export const usePriceQuote = (
  existingPricedQuote: PricingQuoteResponseOutput | null = null,
) => {
  const { getAccessTokenSilently } = useAuth0();
  const basePath = `${window.location.origin}/api`;

  //   TODO: set defaults correctly
  //   TODO: take in an existing quote to hydrate the state;

  const [pricedQuote, setPricedQuote] =
    useState<PricingQuoteResponseOutput | null>(existingPricedQuote);
  const [clientType, setClientType] = useState<ClientType>(
    existingPricedQuote?.request.client_type || ClientType.Downballot,
  );
  const [clientBudget, setClientBudget] = useState<ClientBudget>(
    existingPricedQuote?.request.client_budget || ClientBudget.Medium,
  );
  const [clientComplexity, setClientComplexity] = useState<ClientComplexity>(
    existingPricedQuote?.request.client_complexity || ClientComplexity.Standard,
  );
  const [researchComplexity, setResearchComplexity] = useState<ResearchComplexity>(
    existingPricedQuote?.request.research_complexity || ResearchComplexity.Standard,
  );
  const [businessUnit, setBusinessUnit] = useState<BusinessUnit>(
    (existingPricedQuote?.request as any)?.business_unit || BusinessUnit.Pna,
  );
  const [creativeTesting, setCreativeTesting] = useState<boolean>(
    existingPricedQuote?.request.creative_testing || false,
  );
  const [lowBandwidth, setLowBandwidth] = useState<boolean>(
    (existingPricedQuote?.request as any)?.low_bandwidth || false,
  );
  const [districts, setDistricts] = useState<DistrictOutput[]>(
    existingPricedQuote?.request.districts || [],
  );
  const [universeTypes, setUniverseTypes] = useState<UniverseType[]>(
    existingPricedQuote?.request.universe_types || [
      UniverseType.RegisteredVoters,
    ],
  );
  const [numOfAdditionalCrosstabs, setNumOfAdditionalCrosstabs] = useState<number>(
    existingPricedQuote?.request.num_of_additional_crosstabs || 0,
  );
  const [numOfAdditionalWeightingUniverses, setNumOfAdditionalWeightingUniverses] = useState<number>(
    existingPricedQuote?.request.num_of_additional_weighting_universes || 0,
  );
  const [numOfAdditionalMonitoringUniverses, setNumOfAdditionalMonitoringUniverses] = useState<number>(
    existingPricedQuote?.request.num_of_additional_monitoring_universes || 0,
  );
  const [specialDemoFilterPct, setSpecialDemoFilterPct] = useState<number>(
    existingPricedQuote?.request.special_demo_filter_pct || 1.0,
  );
  const [markupOrDiscountPercentage, setMarkupOrDiscountPercentage] =
    useState<number>(
      existingPricedQuote?.request.markup_or_discount_percentage || 1.0,
    );
  const [absoluteMarkupOrDiscount, setAbsoluteMarkupOrDiscount] =
    useState<number>(
      existingPricedQuote?.request.absolute_markup_or_discount || 0,
    );
  const [sampleSizes, setSampleSizes] = useState<number[]>(
    existingPricedQuote?.request.sample_sizes || [],
  );
  const [numberOfQuestions, setNumberOfQuestions] = useState<number[]>(
    existingPricedQuote?.request.number_of_questions || [],
  );
  const [oversampleType, setOversampleType] = useState<OverSampleType | null>(
    existingPricedQuote?.request.oversample_type || null,
  );
  const [numberOfOversamples, setNumberOfOversamples] = useState<number>(
    existingPricedQuote?.request.number_of_oversamples || 0,
  );
  const [otherUniverseTypeDescription, setOtherUniverseTypeDescription] =
    useState<string>(
      existingPricedQuote?.request.other_universe_type_description || '',
    );
  const [videoLength, setVideoLength] = useState<number>(
    existingPricedQuote?.request.video_length || 0,
  );
  const [suggestedClientBudgets, setSuggestedClientBudgets] = useState<
    ClientBudget[]
  >([ClientBudget.Medium]);
  const [suggestedClientComplexities, setSuggestedClientComplexities] =
    useState<ClientComplexity[]>(
      decideClientComplexitySuggestions(
        (existingPricedQuote?.request as any)?.business_unit || BusinessUnit.Pna,
      ),
    );
  const [clientBudgetReason, setClientBudgetReason] = useState<string>(
    existingPricedQuote?.request.client_budget_reason || '',
  );
  // TODO: set districtsDemoData and suggestedClientTypeTiers correctly on initial load
  const [districtsDemoData, setDistrictsDemoData] = useState<
    DistrictDemoData[]
  >([]);
  const [universeCountErrorMessage, setUniverseCountErrorMessage] =
    useState<string>('');

  const [isPricingLoading, setIsPricingLoading] = useState(false);
  const [pricingErrorMessage, setPricingErrorMessage] = useState('');
  // TODO: when loading an existing quote, set this to true
  const [
    userInputtedSpecialDemoFilterPct,
    setUserInputtedSpecialDemoFilterPct,
  ] = useState(false);
  // TODO: decide if setting the suggested to the existing is the right thing to do
  // likely not to matter since it will get switched to the suggested value after you edit anything
  const [suggestedSpecialDemoFilterPct, setSuggestedSpecialDemoFilterPct] =
    useState<number>(
      existingPricedQuote?.request.special_demo_filter_pct || 1.0,
    );

  // derived state
  const registeredVoterPopulation = districtsDemoData.reduce(
    (prev, curr) => Math.max(prev, curr.registered_voters),
    0,
  );

  const calculatedMaxRecommendedSampleSize =
    calculateMaxRecommendedSampleSize(districtsDemoData, universeTypes);
  const calculatedGeneralMaxRecommendedSampleSize =
    calculateGeneralMaxRecommendedSampleSize(districtsDemoData);
  const calculatedDemPrimaryMaxRecommendedSampleSize =
    calculateDemPrimaryMaxRecommendedSampleSize(districtsDemoData);
  
  const totalUniverses = numOfAdditionalCrosstabs + numOfAdditionalWeightingUniverses + numOfAdditionalMonitoringUniverses + 1;
  const universeCountIsValid =
    totalUniverses > 0 &&
    universeTypes.length > 0 &&
    totalUniverses >= universeTypes.length;

  const showOtherUniverseTypeDescription = universeTypes.includes(
    UniverseType.OtherNonStandard,
  );

  const showClientBudgetReason = isClientBudgetMismatched(
    clientBudget,
    suggestedClientBudgets,
  );

  // Track the prior clientBudget so the cleanup effect can distinguish a
  // user-initiated budget change (where the saved rationale should be cleared)
  // from a hydration-induced recommendation shift (where it should not).
  const prevClientBudgetRef = useRef(clientBudget);

  // functions
  const priceQuote = async (
    request: PricingQuoteRequestInput,
    token = getAccessTokenSilently(),
  ): Promise<AxiosResponse<PricingQuoteResponseOutput>> => {
    if (!canPriceQuote(request)) {
      throw new Error(
        'Cannot price quote with the given request. Please check your inputs.',
      );
    }
    const tokenValue = await token;
    return new PricingApi(
      new Configuration({ basePath, accessToken: tokenValue }),
    ).priceQuotePricingPost(request);
  };

  const decideUniverseCountErrorMessage = () => {
    if (universeTypes.length === 0) {
      return 'You must select at least one universe!';
    }
    if (universeTypes.length > totalUniverses) {
      return `You can only select ${totalUniverses} universe type${
        totalUniverses > 1 ? 's' : ''
      }!`;
    }
    return 'Error with universe types, contact the Tech team.';
  };

  const callPriceQuote = () => {
    const workingQuoteRequest: PricingQuoteRequestInput = {
      client_type: clientType,
      client_budget: clientBudget,
      client_complexity: clientComplexity,
      research_complexity: researchComplexity,
      business_unit: businessUnit,
      creative_testing: creativeTesting,
      low_bandwidth: lowBandwidth,
      districts: _.compact(districts) as DistrictOutput[],
      num_of_additional_crosstabs: numOfAdditionalCrosstabs,
      num_of_additional_weighting_universes: numOfAdditionalWeightingUniverses,
      num_of_additional_monitoring_universes: numOfAdditionalMonitoringUniverses,
      universe_types: universeTypes,
      special_demo_filter_pct: specialDemoFilterPct,
      markup_or_discount_percentage: markupOrDiscountPercentage,
      absolute_markup_or_discount: absoluteMarkupOrDiscount,
      sample_sizes: sampleSizes,
      number_of_questions: numberOfQuestions,
      oversample_type: oversampleType ?? undefined,
      number_of_oversamples: numberOfOversamples,
      other_universe_type_description: otherUniverseTypeDescription,
      client_budget_reason: clientBudgetReason || undefined,
      video_length: videoLength,
      max_sample_size: calculatedMaxRecommendedSampleSize > 0 ? calculatedMaxRecommendedSampleSize : undefined,
    } as PricingQuoteRequestInput;
    const quoteHasChanged =
      pricedQuote && !_.isEqual(pricedQuote.request, workingQuoteRequest);
    const isReadyForPricing =
      canPriceQuote(workingQuoteRequest) &&
      (pricedQuote === null || quoteHasChanged);
    if (isReadyForPricing) {
      setIsPricingLoading(true);
      priceQuote(workingQuoteRequest)
        .then((response) => {
          if (response && response.data) {
            // console.log('existing quote', pricedQuote);
            // console.log('new quote', response.data);
            setPricedQuote(response.data);
            setPricingErrorMessage('');
          } else {
            setPricedQuote(null);
            console.error('no response data', response); // eslint-disable-line no-console
            setPricingErrorMessage(
              'Could not price quote. Please try again or contact Tech team.',
            );
          }
          setIsPricingLoading(false);
        })
        .catch((error) => {
          console.error(error); // eslint-disable-line no-console
          setPricingErrorMessage(
            'Could not price quote. Please try again or contact Tech team.',
          );
          setIsPricingLoading(false);
        });
    } else if (quoteHasChanged && !canPriceQuote(workingQuoteRequest)) {
      setPricedQuote(null);
      setPricingErrorMessage(
        'Check for errors in the form. Please try again or contact Tech team.',
      );
    }
  };

  // effects
  // useEffect(() => {
  //   // TODO: set districtsDemoData correctly
  //   // TODO: set suggestedClientTypeTiers correctly
  //   console.log('first load');
  // }, []);

  useEffect(() => {
    const newSuggestedClientBudgets = decideClientBudgetSuggestions(
      clientType,
      registeredVoterPopulation,
    );
    setSuggestedClientBudgets(newSuggestedClientBudgets);
  }, [clientType, registeredVoterPopulation]);

  useEffect(() => {
    setSuggestedClientComplexities(
      decideClientComplexitySuggestions(businessUnit),
    );
  }, [businessUnit]);

  useEffect(() => {
    if (!universeCountIsValid) {
      setUniverseCountErrorMessage(decideUniverseCountErrorMessage());
    }
    if (!showOtherUniverseTypeDescription) {
      setOtherUniverseTypeDescription('');
    }
    // Only clear the rationale when the user actively changes the budget to
    // one matching the recommendation. Avoids wiping a hydrated reason when
    // the recommendation list asynchronously catches up to loaded districts.
    if (!showClientBudgetReason && prevClientBudgetRef.current !== clientBudget) {
      setClientBudgetReason('');
    }
    prevClientBudgetRef.current = clientBudget;
    const suggestedPct = decideSuggestedSpecialDemoFilterPct(universeTypes);
    setSuggestedSpecialDemoFilterPct(suggestedPct);
    const shouldOverridePct =
      !userInputtedSpecialDemoFilterPct &&
      suggestedPct !== specialDemoFilterPct;
    if (shouldOverridePct) {
      // console.log('overriding special demo filter pct', suggestedPct);
      setSpecialDemoFilterPct(suggestedPct);
    }
  }, [
    universeCountIsValid,
    showOtherUniverseTypeDescription,
    showClientBudgetReason,
    clientBudget,
    universeTypes,
    numOfAdditionalCrosstabs,
    numOfAdditionalWeightingUniverses,
    numOfAdditionalMonitoringUniverses,
    specialDemoFilterPct,
  ]);

  useEffect(() => {
    callPriceQuote();
  }, [
    clientType,
    clientBudget,
    clientComplexity,
    researchComplexity,
    businessUnit,
    creativeTesting,
    lowBandwidth,
    districts,
    markupOrDiscountPercentage,
    numberOfOversamples,
    numberOfQuestions,
    numOfAdditionalCrosstabs,
    numOfAdditionalWeightingUniverses,
    numOfAdditionalMonitoringUniverses,
    otherUniverseTypeDescription,
    clientBudgetReason,
    sampleSizes,
    specialDemoFilterPct,
    universeTypes,
    videoLength,
    absoluteMarkupOrDiscount,
  ]);

  return {
    absoluteMarkupOrDiscount,
    calculatedMaxRecommendedSampleSize,
    calculatedGeneralMaxRecommendedSampleSize,
    calculatedDemPrimaryMaxRecommendedSampleSize,
    callPriceQuote,
    canPriceQuote,
    clientType,
    clientBudget,
    clientComplexity,
    businessUnit,
    creativeTesting,
    lowBandwidth,
    districts,
    districtsDemoData,
    isPricingLoading,
    markupOrDiscountPercentage,
    numberOfOversamples,
    numberOfQuestions,
    numOfAdditionalCrosstabs,
    numOfAdditionalWeightingUniverses,
    numOfAdditionalMonitoringUniverses,
    otherUniverseTypeDescription,
    researchComplexity,
    pricedQuote,
    pricingErrorMessage,
    registeredVoterPopulation,
    sampleSizes,
    setAbsoluteMarkupOrDiscount,
    setClientType,
    setClientBudget,
    setClientComplexity,
    setBusinessUnit,
    setCreativeTesting,
    setLowBandwidth,
    setDistricts,
    setDistrictsDemoData,
    setIsPricingLoading,
    setMarkupOrDiscountPercentage,
    setNumberOfOversamples,
    setNumberOfQuestions,
    setNumOfAdditionalCrosstabs,
    setNumOfAdditionalWeightingUniverses,
    setNumOfAdditionalMonitoringUniverses,
    setOtherUniverseTypeDescription,
    setResearchComplexity,
    setPricedQuote,
    setPricingErrorMessage,
    setSampleSizes,
    setSpecialDemoFilterPct,
    setUniverseCountErrorMessage,
    setUniverseTypes,
    setUserInputtedSpecialDemoFilterPct,
    setVideoLength,
    showOtherUniverseTypeDescription,
    specialDemoFilterPct,
    suggestedClientBudgets,
    suggestedClientComplexities,
    suggestedSpecialDemoFilterPct,
    showClientBudgetReason,
    clientBudgetReason,
    setClientBudgetReason,
    totalUniverses,
    universeCountErrorMessage,
    universeCountIsValid,
    universeTypes,
    userInputtedSpecialDemoFilterPct,
    videoLength,
    oversampleType,
    setOversampleType,
  };
};
